import type { CommunityDetail } from 'models';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canInviteToCommunityProfile, canManageCommunityProfile } from 'permissions';
import { Button, Card, Col, Row, Skeleton, Title } from 'ui';
import { useUser } from 'features/auth';
import { useMembersQuery } from '../../queries';
import { InviteFriendsModal } from '../invite-friends-modal';
import { UserList } from '../user-list';
import { useDeleteMember } from './hooks';

type P = {
  community: CommunityDetail;
};

const PAGE_SIZE = 20;

const Members = ({ community }: P) => {
  const { t } = useTranslation();
  const [isOpenInviteModal, setIsOpenInviteModal] = useState(false);

  const { railsUserData: viewer } = useUser();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading: isLoadingMembers,
  } = useMembersQuery({
    communityId: community.id,
    pageSize: PAGE_SIZE,
  });

  const { confirm: onDeleteUser, contextHolder } = useDeleteMember({ community });

  const members = useMemo(() => data?.pages.flat().flat(), [data]);

  return (
    <>
      {contextHolder}
      <Card
        className="community-members"
        title={
          <Row align="middle" justify="space-between">
            <Title className="community-members__title" level={4} marginBottom>
              {t('Members ({{count}})', { count: community.membersCount })}
            </Title>
            {canInviteToCommunityProfile({ community, viewer }) ? (
              <Button
                onClick={() => {
                  setIsOpenInviteModal(true);
                }}
                size="large"
                type="link-primary"
              >
                Invite friends
              </Button>
            ) : null}
          </Row>
        }
      >
        {isLoadingMembers || !members ? (
          <Skeleton active avatar paragraph={{ rows: 18 }} />
        ) : (
          <>
            <Row>
              <Col span={24}>
                <UserList
                  onDeleteUser={canManageCommunityProfile({ community, viewer }) ? onDeleteUser : undefined}
                  users={members}
                />
              </Col>
            </Row>
            {hasNextPage ? (
              <Row align="middle" className="community-members__show-more" justify="center">
                <Button loading={isFetchingNextPage || isFetching} onClick={async () => fetchNextPage()} size="large">
                  Show More
                </Button>
              </Row>
            ) : null}
          </>
        )}
      </Card>
      <InviteFriendsModal
        community={community}
        isOpened={isOpenInviteModal}
        onCancel={() => {
          setIsOpenInviteModal(false);
        }}
      />
    </>
  );
};

export default Members;
